import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Images, Search, RefreshCw, Trash2, Loader2, ImageOff, X, AlertTriangle,
  ExternalLink, Sparkles, Camera, Upload as UploadIcon, HardDrive,
} from 'lucide-react';
import Button from '@/components/ui/Button';
import Modal from '@/components/ui/Modal';
import {
  useMediaAssets, useMediaFacets, useMediaAsset, useUpdateAsset, useDeleteAsset,
  useReconcileMedia, formatBytes, SOURCE_LABELS,
  type MediaAsset, type AssetSource,
} from '@/hooks/useMedia';
import { cn } from '@/lib/utils';
import Tooltip from '@/components/ui/Tooltip';

/**
 * Media Library — everything this site has, and what would break if you removed it.
 *
 * Files were previously write-only: uploaded to MinIO, the URL handed to
 * whichever row asked for it, and never recorded anywhere. You could not see
 * what existed, could not re-use it, and could not find out where an image was
 * used before deleting it. The last one is the reason the delete here is
 * guarded rather than immediate.
 *
 * Alt text lives here too. It has had nowhere to live for anything except
 * product images, which is why almost every CMS image on the fleet ships
 * without one.
 */

const SOURCE_ICON: Record<AssetSource, typeof Images> = {
  upload: UploadIcon,
  ai: Sparkles,
  stock: Camera,
  brand_kit: Images,
  import: Images,
  unknown: HardDrive,
};

export default function MediaLibrary() {
  const [q, setQ] = useState('');
  const [source, setSource] = useState<AssetSource | ''>('');
  const [folder, setFolder] = useState('');
  const [unused, setUnused] = useState(false);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);

  const filters = { q: q.trim() || undefined, source: source || undefined, folder: folder || undefined, unused, page };
  const { data, isLoading } = useMediaAssets(filters);
  const { data: facets } = useMediaFacets();
  const reconcile = useReconcileMedia();

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  function resetTo(fn: () => void) {
    fn();
    setPage(1);
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <Images className="h-6 w-6 text-sage-600" aria-hidden="true" />
            Media Library
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Every image on this site, where it came from, and where it's used.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {reconcile.data && (
            <span className="text-xs text-gray-500">
              Found {reconcile.data.indexed} new
              {reconcile.data.missing > 0 && `, ${reconcile.data.missing} missing`}
            </span>
          )}
          <Button
            variant="secondary"
            onClick={() => reconcile.mutate(false)}
            disabled={reconcile.isPending}
            /* An existing site's bucket predates this index, so the library
               opens empty until this runs. Also picks up anything written by a
               background job (brand kits, wizard seeds). */
            title="Look for files in storage that aren't listed here yet"
          >
            {reconcile.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
            )}
            <span className="ml-2">Scan storage</span>
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            aria-hidden="true"
          />
          <input
            className="w-full rounded-lg border border-gray-300 py-2 pl-8 pr-3 text-sm focus:border-sage-500 focus:outline-none focus:ring-1 focus:ring-sage-500"
            placeholder="Search by name, alt text or file"
            value={q}
            onChange={(e) => resetTo(() => setQ(e.target.value))}
            aria-label="Search media"
          />
        </div>

        <select
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sage-500 focus:outline-none focus:ring-1 focus:ring-sage-500"
          value={folder}
          onChange={(e) => resetTo(() => setFolder(e.target.value))}
          aria-label="Filter by folder"
        >
          <option value="">All folders</option>
          {facets?.folders.map((f) => (
            <option key={f.folder} value={f.folder}>
              {f.folder} ({f.count})
            </option>
          ))}
        </select>

        <select
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sage-500 focus:outline-none focus:ring-1 focus:ring-sage-500"
          value={source}
          onChange={(e) => resetTo(() => setSource(e.target.value as AssetSource | ''))}
          aria-label="Filter by where it came from"
        >
          <option value="">Any source</option>
          {facets?.sources.map((s) => (
            <option key={s.source} value={s.source}>
              {SOURCE_LABELS[s.source]} ({s.n})
            </option>
          ))}
        </select>

        <label className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-sage-600 focus:ring-sage-500"
            checked={unused}
            onChange={(e) => resetTo(() => setUnused(e.target.checked))}
          />
          Unused only
          <Tooltip content="Images with no reference anywhere we track: products, pages, homepage sections, blog posts and settings. An image used only inside custom HTML or an email template can still show as unused, so check where it came from before deleting." />
        </label>
      </div>

      {data?.truncated && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            There are more files than this view checked. Narrow it with a folder or a search
            to be sure you're seeing everything unused.
          </span>
        </div>
      )}

      {isLoading && (
        <div className="py-16 text-center text-gray-500">
          <Loader2 className="mx-auto h-7 w-7 animate-spin" aria-hidden="true" />
        </div>
      )}

      {!isLoading && data && data.assets.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 py-16 text-center">
          <ImageOff className="mx-auto mb-3 h-8 w-8 text-gray-300" aria-hidden="true" />
          <p className="text-gray-600">
            {q || source || folder || unused
              ? 'Nothing matches those filters.'
              : 'Nothing indexed yet.'}
          </p>
          {!q && !source && !folder && !unused && (
            <p className="mt-1 text-sm text-gray-500">
              If this site already has images, press <strong>Scan storage</strong> to find them.
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {data?.assets.map((asset) => (
          <Tile key={asset.id} asset={asset} onOpen={() => setSelected(asset.id)} />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="secondary"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {selected && <DetailModal id={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function Tile({ asset, onOpen }: { asset: MediaAsset; onOpen: () => void }) {
  const Icon = SOURCE_ICON[asset.source] ?? Images;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group overflow-hidden rounded-lg border border-gray-200 bg-white text-left shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-sage-500"
    >
      <div className="relative">
        {asset.url ? (
          <img
            src={asset.url}
            alt=""
            loading="lazy"
            className="aspect-[4/3] w-full bg-gray-100 object-cover"
          />
        ) : (
          <div className="grid aspect-[4/3] w-full place-items-center bg-gray-100">
            <ImageOff className="h-6 w-6 text-gray-300" aria-hidden="true" />
          </div>
        )}
        <span
          className={cn(
            'absolute right-1.5 top-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium',
            asset.usage_count > 0 ? 'bg-sage-600 text-white' : 'bg-gray-900/60 text-white',
          )}
          title={
            asset.usage_count > 0
              ? `Used in ${asset.usage_count} place${asset.usage_count === 1 ? '' : 's'}`
              : 'Not used anywhere'
          }
        >
          {asset.usage_count > 0 ? `${asset.usage_count} use${asset.usage_count === 1 ? '' : 's'}` : 'unused'}
        </span>
      </div>
      <div className="p-2">
        <p className="truncate text-xs font-medium text-gray-900" title={asset.key}>
          {asset.title || asset.key.split('/').pop()}
        </p>
        <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-gray-500">
          <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
          {SOURCE_LABELS[asset.source]}
          {asset.width && asset.height && ` · ${asset.width}×${asset.height}`}
        </p>
        {!asset.alt_text && (
          /* Surfaced on the tile, not buried in the detail panel: a missing alt
             is invisible until someone using a screen reader hits it. */
          <p className="mt-0.5 text-[11px] text-amber-600">No alt text</p>
        )}
      </div>
    </button>
  );
}

function DetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: asset, isLoading } = useMediaAsset(id);
  const update = useUpdateAsset();
  const remove = useDeleteAsset();
  const [alt, setAlt] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const altValue = alt ?? asset?.alt_text ?? '';
  const inUse = (asset?.used_on.length ?? 0) > 0;

  async function doDelete(force: boolean) {
    setDeleteError('');
    try {
      await remove.mutateAsync({ id, force });
      onClose();
    } catch (err) {
      setDeleteError(
        err instanceof Error && err.message ? err.message : 'Could not delete that file.',
      );
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={asset?.title || 'Image'} className="max-w-3xl">
      {isLoading || !asset ? (
        <div className="py-12 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-gray-400" aria-hidden="true" />
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            {asset.url && (
              <img
                src={asset.url}
                alt={asset.alt_text ?? ''}
                className="w-full rounded-lg border border-gray-200 bg-gray-100 object-contain"
              />
            )}
            <dl className="mt-3 space-y-1 text-xs text-gray-600">
              <div className="flex justify-between gap-3">
                <dt>Size</dt>
                <dd>
                  {asset.width && asset.height ? `${asset.width} × ${asset.height} · ` : ''}
                  {formatBytes(asset.bytes)}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt>Source</dt>
                <dd>{SOURCE_LABELS[asset.source]}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt>File</dt>
                <dd className="truncate font-mono" title={asset.key}>
                  {asset.key}
                </dd>
              </div>
            </dl>
            <Provenance asset={asset} />
          </div>

          <div className="space-y-4">
            <div>
              <label
                className="mb-1 block text-xs font-medium text-gray-700"
                htmlFor="media-alt"
              >
                Alt text
              </label>
              <textarea
                id="media-alt"
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-sage-500 focus:outline-none focus:ring-1 focus:ring-sage-500"
                value={altValue}
                onChange={(e) => setAlt(e.target.value)}
                placeholder="Describe the image for someone who can't see it"
              />
              <div className="mt-2 flex items-center gap-2">
                <Button
                  onClick={() => update.mutate({ id, alt_text: altValue.trim() || null })}
                  disabled={update.isPending || alt === null}
                >
                  {update.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                  Save
                </Button>
                {update.isSuccess && alt !== null && (
                  <span className="text-xs text-sage-700">Saved</span>
                )}
              </div>
            </div>

            <div>
              <h3 className="mb-1.5 text-xs font-medium text-gray-700">
                {inUse ? `Used in ${asset.used_on.length}` : 'Not used anywhere'}
              </h3>
              {inUse ? (
                <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
                  {asset.used_on.map((u, i) => (
                    <li key={`${u.entity_type}-${u.entity_id}-${i}`}>
                      {u.admin_path ? (
                        <Link
                          to={u.admin_path}
                          className="flex items-center gap-1 text-sage-700 hover:underline"
                          onClick={onClose}
                        >
                          <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
                          <span className="truncate">
                            {u.label || u.entity_type.replace(/_/g, ' ')}
                          </span>
                        </Link>
                      ) : (
                        <span className="text-gray-600">{u.entity_type}</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">Safe to delete.</p>
              )}
            </div>

            {deleteError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {deleteError}
              </div>
            )}

            {!confirmDelete ? (
              <Button variant="secondary" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                <span className="ml-2">Delete</span>
              </Button>
            ) : (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="mb-2 text-sm text-red-800">
                  {inUse
                    ? `This is on the live site in ${asset.used_on.length} place${asset.used_on.length === 1 ? '' : 's'}. Deleting it breaks those. This can't be undone.`
                    : 'Delete this file permanently? This can’t be undone.'}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="danger"
                    onClick={() => doDelete(inUse)}
                    disabled={remove.isPending}
                  >
                    {remove.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    )}
                    {inUse ? 'Delete anyway' : 'Delete'}
                  </Button>
                  <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
                    <X className="h-4 w-4" aria-hidden="true" />
                    <span className="ml-1.5">Keep it</span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * What made this image.
 *
 * For an AI generation that is the prompt, model and seed — enough to run it
 * again or vary it. For a stock photo it is the photographer credit, which is a
 * licence obligation rather than trivia.
 */
function Provenance({ asset }: { asset: MediaAsset }) {
  const meta = asset.source_meta ?? {};
  const str = (k: string) => (typeof meta[k] === 'string' ? (meta[k] as string) : null);

  if (asset.source === 'ai' && str('prompt')) {
    return (
      <div className="mt-3 rounded-lg bg-gray-50 p-2.5 text-xs text-gray-600">
        <p className="mb-1 font-medium text-gray-700">Prompt</p>
        <p className="mb-2 whitespace-pre-wrap">{str('prompt')}</p>
        {str('negative_prompt') && (
          <p className="mb-2">
            <span className="font-medium text-gray-700">Kept out: </span>
            {str('negative_prompt')}
          </p>
        )}
        <p className="font-mono text-[11px]">
          {str('model')}
          {typeof meta.seed === 'number' && ` · seed ${meta.seed}`}
        </p>
      </div>
    );
  }

  if (asset.source === 'stock' && str('photographer')) {
    return (
      <div className="mt-3 rounded-lg bg-gray-50 p-2.5 text-xs text-gray-600">
        <p className="font-medium text-gray-700">Photo credit</p>
        <p>
          {str('photographer')} on {str('source') ?? 'stock'}
        </p>
        {str('page_url') && (
          <a
            href={str('page_url')!}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1 text-sage-700 hover:underline"
          >
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
            View original
          </a>
        )}
        {/* Not optional: Unsplash's terms require this credit wherever the photo
            appears, so it travels with the file rather than the page. */}
        <p className="mt-1 text-[11px] text-gray-500">
          This credit is shown wherever the photo is used.
        </p>
      </div>
    );
  }

  return null;
}
